import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Trip } from "../../api/trips";
import { Avatar } from "../../components/common/Avatar";
import { withWidth } from "../../lib/photoUrl";

function formatRoute(trip: Trip): string {
  const parts = [trip.startLocation, trip.endLocation].filter(Boolean);
  if (parts.length >= 2) return parts.join(" → ");
  return trip.title;
}

function dayCount(trip: Trip): number | null {
  if (!trip.startDate || !trip.endDate) return null;
  const start = new Date(trip.startDate);
  const end = new Date(trip.endDate);
  const diff = Math.round(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
  );
  return diff > 0 ? diff : null;
}

function firstName(name: string): string {
  return name.split(/\s+/)[0] || name;
}

function ContributorsRow({
  trip,
  currentUserId,
}: {
  trip: Trip;
  currentUserId?: string;
}) {
  const { t } = useTranslation("trips");
  const ownerName = trip.ownerName;
  if (!ownerName) return null;

  const editors = trip.editors ?? [];
  const isOwner = currentUserId === trip.ownerId;
  const isEditor =
    !isOwner && trip.role === "write";

  const displayName = isOwner ? t("trip.you") : firstName(ownerName);

  let editorLabel: string | null = null;
  if (isEditor) {
    if (editors.length > 1) {
      editorLabel = t("trip.andCount", { count: editors.length - 1 });
    } else {
      editorLabel = t("trip.you");
    }
  } else if (editors.length === 1) {
    editorLabel = firstName(editors[0].name);
  } else if (editors.length > 1) {
    editorLabel = t("trip.othersCount", { count: editors.length });
  }

  return (
    <div className="trip-card__contributors" data-testid="trip-card-contributors">
      <Avatar
        name={ownerName}
        image={withWidth(trip.ownerImage, 48)}
        size={22}
        data-testid="trip-card-owner-avatar"
      />
      {editors.slice(0, 2).map((e, i) => (
        <Avatar
          key={i}
          name={e.name}
          image={withWidth(e.image, 48)}
          size={22}
          className="trip-card__contributors-avatar--stacked"
        />
      ))}
      {editors.length > 2 && (
        <span className="trip-card__contributors-more">+{editors.length - 2}</span>
      )}
      <span className="trip-card__contributors-name">
        {displayName}
        {editorLabel && ` & ${editorLabel}`}
      </span>
    </div>
  );
}

export function TripCard({
  trip,
  currentUserId,
  onArchive,
  onDelete,
  onRestore,
  showDelete,
}: {
  trip: Trip;
  currentUserId?: string;
  onArchive?: (trip: Trip) => void;
  onDelete?: (trip: Trip) => void;
  onRestore?: (trip: Trip) => void;
  showDelete?: boolean;
}) {
  const { t } = useTranslation("trips");
  const navigate = useNavigate();
  const coverPhoto = withWidth(trip.photoUrl ?? trip.coverPhotos[0]?.photoUrl ?? null, 480);
  const days = dayCount(trip);
  const stops = trip.stops ?? 0;
  const isOwner = trip.role === "owner";

  return (
    <div
      className="trip-card"
      data-testid={`trip-card-${trip.id}`}
      onClick={() => navigate(`/trips/${trip.id}`)}
    >
      <div className="trip-card__photo">
        {coverPhoto ? (
          <img
            src={coverPhoto}
            alt=""
            className="trip-card__photo-img"
            loading="lazy"
          />
        ) : (
          <div
            className="trip-card__photo-placeholder"
            data-testid="trip-card-photo-placeholder"
          />
        )}
        {isOwner && !showDelete && onArchive && (
          <button
            type="button"
            className="trip-card__action-btn"
            data-testid={`archive-btn-${trip.id}`}
            aria-label={`Archive ${trip.title}`}
            onClick={(e) => {
              e.stopPropagation();
              onArchive(trip);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M2 4h12v1H2V4zm1 2v7a1 1 0 001 1h8a1 1 0 001-1V6H3zm4 2h2v4H7V8z"
                fill="currentColor"
              />
            </svg>
          </button>
        )}
        {isOwner && showDelete && onRestore && (
          <button
            type="button"
            className="trip-card__unarchive-btn"
            data-testid={`restore-btn-${trip.id}`}
            aria-label={t("trip.restoreTooltip")}
            onClick={(e) => {
              e.stopPropagation();
              onRestore(trip);
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path
                d="M2 8a6 6 0 1 1 1.05 3.36l.87-.5A5 5 0 1 0 3 8H5L2.5 5 0 8h2zm5-3v3.5l2.5 1.5.5-.87-2-1.2V5H7z"
                fill="currentColor"
              />
            </svg>
            {t("trip.unarchive")}
          </button>
        )}
        {isOwner && showDelete && onDelete && (
          <button
            type="button"
            className="trip-card__action-btn trip-card__action-btn--danger"
            data-testid={`delete-btn-${trip.id}`}
            aria-label={`Delete ${trip.title}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(trip);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M5 2V1h6v1h3v1H2V2h3zm1 3v8h1V5H6zm3 0v8h1V5H9zM3 4v10a1 1 0 001 1h8a1 1 0 001-1V4H3z"
                fill="currentColor"
              />
            </svg>
          </button>
        )}
      </div>
      <div className="trip-card__body">
        <h3 className="trip-card__title">{trip.title}</h3>
        <ContributorsRow trip={trip} currentUserId={currentUserId} />
        <p className="trip-card__route">{formatRoute(trip)}</p>
        <div className="trip-card__meta">
          {days && <span>{t("trip.days", { count: days })}</span>}
          {stops > 0 && <span>{t("trip.stops", { count: stops })}</span>}
          {trip.collaboratorCount > 0 && (
            <span className="trip-card__people">+{trip.collaboratorCount}</span>
          )}
        </div>
      </div>
    </div>
  );
}
